import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Sparkles, Play, Pause, RotateCcw, ArrowRight, MessageCircle } from "lucide-react";

const DURATION = 30000;
const SCENE_ENDS = [4000, 9000, 14000, 21000, 26000, 30000];

const CAPTIONS = [
  "Your customer finds you on social.",
  "They see the details before messaging you.",
  "They choose what they need.",
  "A few details — no back-and-forth.",
  "They continue to WhatsApp with a clearer enquiry.",
];

const SERVICES = [
  { title: "Gel Manicure", price: "EC$120", duration: "75 minutes" },
  { title: "Spa Pedicure", price: "EC$150", duration: "60 minutes" },
  { title: "Gel Removal + Refresh", price: "EC$75", duration: "30 minutes" },
];

const NOTE_TEXT = "Morning preferred";

const TapDot = () => (
  <motion.span
    initial={{ scale: 0.4, opacity: 0 }}
    animate={{ scale: [0.4, 1, 1.6], opacity: [0, 1, 0] }}
    transition={{ duration: 1.1, repeat: Infinity, ease: "easeOut" }}
    className="pointer-events-none absolute -right-1 -top-1 z-10 h-6 w-6 rounded-full bg-guinep-deep/30 ring-2 ring-guinep-deep/60"
  />
);

const SocialScene = () => (
  <div className="rounded-2xl border border-guinep-line/70 bg-white p-4" data-testid="demo-scene-social">
    <div className="flex items-center gap-2.5">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-guinep-soft font-serif text-sm text-guinep-deep">I</div>
      <div>
        <p className="text-[13px] font-semibold text-guinep-ink">islandglowstudio</p>
        <p className="text-[10px] text-guinep-mute">St. John's, Antigua</p>
      </div>
    </div>
    <div className="mt-3 flex h-36 items-center justify-center rounded-xl bg-guinep-soft">
      <p className="font-serif text-lg italic text-guinep-mid">Gel sets from EC$120</p>
    </div>
    <p className="mt-3 text-[13px] text-guinep-ink">
      <span className="font-semibold">islandglowstudio</span> Friday slots just opened — link in bio
    </p>
    <div className="relative mt-3 inline-flex items-center gap-1.5 rounded-lg bg-guinep-soft px-3 py-2">
      <TapDot />
      <span className="text-[13px] font-semibold text-guinep-mid">guinepseed.com/islandglow</span>
    </div>
  </div>
);

const BusinessPage = ({ highlight = false, tapInterested = false }) => (
  <div data-testid="demo-scene-page">
    <div className="flex flex-col items-center text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white font-serif text-xl font-medium text-guinep-deep shadow-sm">I</div>
      <div className="mt-2.5 flex items-center gap-2">
        <p className="font-serif text-xl font-medium tracking-tight text-guinep-ink">Island Glow Studio</p>
        <span className="rounded-full bg-guinep-soft px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-guinep-mid">Demo</span>
      </div>
      <p className="mt-1 flex items-center gap-1 text-[11px] text-guinep-mute">
        <MapPin size={11} /> St. John's, Antigua
      </p>
    </div>

    <div className="mt-4 rounded-xl border border-guinep-line/70 bg-guinep-soft p-3">
      <div className="flex items-center gap-1.5">
        <Sparkles size={11} className="text-guinep-mid" />
        <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-guinep-mid">Availability</span>
      </div>
      <p className="mt-1 font-serif text-sm text-guinep-ink">3 appointments available Friday</p>
    </div>

    <div className="mt-4 space-y-2.5">
      {SERVICES.map((s, i) => (
        <motion.div
          key={s.title}
          animate={highlight && i === 0 ? { scale: 1.02 } : { scale: 1 }}
          className={`rounded-xl border bg-white p-3 transition-colors ${
            highlight && i === 0 ? "border-guinep-deep shadow-[0_8px_24px_-10px_rgba(31,58,43,0.4)]" : "border-guinep-line/70"
          }`}
        >
          <p className="text-[13px] font-semibold text-guinep-ink">{s.title}</p>
          <p className="mt-0.5 text-[11px] text-guinep-mute">
            <span className="font-semibold text-guinep-mid">From {s.price}</span> · {s.duration}
          </p>
          {i === 0 && (
            <div className="relative mt-2.5">
              {tapInterested && <TapDot />}
              <div
                className={`rounded-lg border py-2 text-center text-xs font-semibold transition-colors ${
                  tapInterested ? "border-guinep-deep bg-guinep-soft text-guinep-deep" : "border-guinep-line text-guinep-ink"
                }`}
              >
                I'm Interested
              </div>
            </div>
          )}
        </motion.div>
      ))}
    </div>

    <div className="mt-4 rounded-xl border border-guinep-line/70 bg-white p-3">
      <p className="text-xs font-semibold text-guinep-ink">Deposit policy</p>
      <p className="mt-0.5 text-[11px] text-guinep-mute">Deposit required to confirm appointments.</p>
    </div>
  </div>
);

const EnquiryScene = ({ t }) => {
  const noteLen = t > 4200 ? Math.min(NOTE_TEXT.length, Math.floor((t - 4200) / 110)) : 0;
  const field = "w-full rounded-xl border border-guinep-line bg-white px-3.5 py-2.5 text-[13px] text-guinep-ink";
  const active = "border-guinep-deep bg-guinep-soft";
  return (
    <div className="rounded-2xl border border-guinep-line/70 bg-white p-4" data-testid="demo-scene-enquiry">
      <p className="font-serif text-lg font-medium text-guinep-ink">Enquire about Gel Manicure</p>
      <div className="mt-4 space-y-3.5">
        <div>
          <p className="mb-1 text-[11px] font-semibold text-guinep-ink">Service</p>
          <div className={`${field} ${t >= 0 ? active : ""}`}>Gel Manicure</div>
        </div>
        <div className={t > 1200 ? "opacity-100 transition-opacity duration-500" : "opacity-40 transition-opacity duration-500"}>
          <p className="mb-1 text-[11px] font-semibold text-guinep-ink">Preferred date</p>
          <div className={`${field} ${t > 1200 ? active : ""}`}>
            {t > 1200 ? "Friday, September 11" : "dd/mm/yyyy"}
          </div>
        </div>
        <div className={t > 2800 ? "opacity-100 transition-opacity duration-500" : "opacity-40 transition-opacity duration-500"}>
          <p className="mb-1 text-[11px] font-semibold text-guinep-ink">New or existing client?</p>
          <div className="relative flex gap-2">
            {t > 2800 && t < 4200 && <TapDot />}
            <span className={`flex-1 rounded-lg border py-2 text-center text-xs font-semibold ${t > 2800 ? "border-guinep-deep bg-guinep-soft text-guinep-deep" : "border-guinep-line text-guinep-mute"}`}>New client</span>
            <span className="flex-1 rounded-lg border border-guinep-line py-2 text-center text-xs font-semibold text-guinep-mute">Existing client</span>
          </div>
        </div>
        <div className={t > 4200 ? "opacity-100 transition-opacity duration-500" : "opacity-40 transition-opacity duration-500"}>
          <p className="mb-1 text-[11px] font-semibold text-guinep-ink">Note (optional)</p>
          <div className={field}>{NOTE_TEXT.slice(0, noteLen)}{noteLen < NOTE_TEXT.length && t > 4200 && <span className="animate-pulse">|</span>}</div>
        </div>
        <div className="relative pt-1">
          {t > 5900 && <TapDot />}
          <motion.div
            animate={t > 5900 ? { scale: [1, 1.03, 1] } : {}}
            className={`flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white ${t > 5900 ? "bg-wa-dark" : "bg-wa"}`}
          >
            <MessageCircle size={15} /> Continue to WhatsApp
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const HandoffScene = () => (
  <div data-testid="demo-scene-handoff">
    <div className="rounded-2xl border border-guinep-line/60 bg-guinep-soft p-4">
      <div className="ml-auto max-w-[92%] rounded-2xl rounded-br-sm bg-[#DCF8C6] px-4 py-3 shadow-sm">
        <p className="whitespace-pre-line text-sm leading-relaxed text-guinep-ink">{`Hi Island Glow Studio,

I'm interested in:
Gel Manicure

Preferred date:
Friday

Client:
New client

Note:
Morning preferred`}</p>
      </div>
    </div>
  </div>
);

const EndScene = () => (
  <div className="pt-6 text-center" data-testid="demo-scene-end">
    <h2 className="font-serif text-3xl font-medium tracking-tight text-guinep-ink">That's the handoff.</h2>
    <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-guinep-mute">
      Your client reaches WhatsApp with the important details already included.
    </p>
    <Link
      to="/signup"
      data-testid="demo-signup-button"
      className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-guinep-deep py-3.5 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid"
    >
      Create your Guinep Seed page
      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
    </Link>
  </div>
);

const DemoPage = () => {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const rafRef = useRef(null);
  const lastTick = useRef(null);

  useEffect(() => {
    if (!playing) return;
    lastTick.current = performance.now();
    const tick = (now) => {
      const delta = now - lastTick.current;
      lastTick.current = now;
      setElapsed((e) => {
        const next = Math.min(DURATION, e + delta);
        if (next >= DURATION) setPlaying(false);
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing]);

  const restart = () => {
    setElapsed(0);
    setPlaying(true);
  };

  const sceneIdx = SCENE_ENDS.findIndex((end) => elapsed < end);
  const scene = sceneIdx === -1 ? 5 : sceneIdx;
  const sceneT = elapsed - (scene > 0 ? SCENE_ENDS[scene - 1] : 0);

  return (
    <div className="min-h-screen bg-[#F7F7F5] font-sans" data-testid="demo-page">
      <div className="border-b border-guinep-line/50 bg-white" data-testid="demo-banner">
        <div className="mx-auto flex max-w-sm items-center justify-center gap-2 px-5 py-2.5">
          <span className="rounded-full bg-guinep-deep px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
            30-second demo
          </span>
          <span className="text-xs text-guinep-mute">Try Guinep Seed as your customer would.</span>
        </div>
        <div className="h-0.5 bg-guinep-line/50">
          <div className="h-full bg-guinep-deep transition-[width] duration-100" style={{ width: `${(elapsed / DURATION) * 100}%` }} data-testid="demo-progress" />
        </div>
      </div>

      <div className="mx-auto max-w-sm px-5 pt-6 pb-10">
        <AnimatePresence mode="wait">
          {scene < 5 && (
            <motion.p
              key={scene}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }}
              className="mb-4 text-center font-serif text-lg italic text-guinep-mid"
              data-testid="demo-caption"
            >
              {CAPTIONS[Math.min(scene, CAPTIONS.length - 1)]}
            </motion.p>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.div
            key={scene}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            {scene === 0 && <SocialScene />}
            {scene === 1 && <BusinessPage />}
            {scene === 2 && <BusinessPage highlight tapInterested={sceneT > 1800} />}
            {scene === 3 && <EnquiryScene t={sceneT} />}
            {scene === 4 && <HandoffScene />}
            {scene >= 5 && <EndScene />}
          </motion.div>
        </AnimatePresence>

        <div className="mt-7 flex items-center justify-center gap-3" data-testid="demo-controls">
          <button
            data-testid="demo-playpause"
            onClick={() => (elapsed >= DURATION ? restart() : setPlaying((p) => !p))}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-guinep-deep text-white transition-colors hover:bg-guinep-mid"
            aria-label={playing ? "Pause demo" : "Play demo"}
          >
            {playing ? <Pause size={17} /> : <Play size={17} className="ml-0.5" />}
          </button>
          <button
            data-testid="demo-restart"
            onClick={restart}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-guinep-line bg-white text-guinep-ink transition-colors hover:bg-guinep-soft"
            aria-label="Restart demo"
          >
            <RotateCcw size={16} />
          </button>
        </div>
        {scene >= 5 && (
          <p className="mt-4 text-center">
            <button data-testid="demo-watch-again" onClick={restart} className="text-sm font-semibold text-guinep-mid underline-offset-4 transition-colors hover:text-guinep-deep hover:underline">
              Watch again
            </button>
          </p>
        )}
        <p className="mt-6 text-center text-[11px] text-guinep-mute/60">Demonstration content — no real messages are sent.</p>
      </div>
    </div>
  );
};

export default DemoPage;
